import { FieldArray, Field } from "formik";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { Plus, Trash2, Copy, Ship } from "lucide-react";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import PortAutoComplete from "./PortAutoComplete";
import { handleKeyPress } from "../../functions/Utils";
import { OCEAN_SERVICE_TYPE } from "../../utils/constant";

const CELL_INPUT_CLS =
  "h-[38px] px-2.5 text-sm text-gray-800 bg-white border border-gray-200 rounded-lg outline-none w-full focus:border-yellow-400";

// Whole-day difference between departure and arrival, used to auto-fill
// Transit whenever either date changes — KAM can still override it by hand
// afterwards, this just saves re-typing the obvious case.
const computeTransitDays = (departure, arrival) => {
  if (!departure || !arrival) return "";
  const days = dayjs(arrival).startOf("day").diff(dayjs(departure).startOf("day"), "day");
  return days >= 0 ? days : "";
};

const emptyRoutingLeg = () => ({
  carrier: "",
  vesselName: "",
  voyageNumber: "",
  originPort: "",
  destinationPort: "",
  departure: null,
  arrival: null,
  transshipment: false,
});

// Freight/container rates are entered as regular line items in Buy/Sell
// Charges (e.g. an "Ocean Freight" charge) instead of duplicated per option
// here, so a freight option is just carrier + validity + routing.
export const emptyFreightOption = () => ({
  carrier: "",
  departureDateAndTime: null,
  arrivalDateAndTime: null,
  transitDays: "",
  serviceType: "CY/CY",
  isDirect: true,
  routing: [emptyRoutingLeg()],
});

// "Schedules" tab — despite the formik field staying named `schedules` (to
// avoid rippling a rename through ChargesSection/index.js), each row is a
// full freight option: carrier + validity window and one or more routing
// legs (direct or transshipment). buildPayload (index.js) maps this array to
// the submit payload's `freightCharges[]`.
const SchedulesForm = ({ formikData, portList, carrierList, readOnly }) => {
  const { values, setFieldValue } = formikData;
  const schedules = values.schedules || [];

  return (
    <FieldArray name="schedules">
      {({ remove, push, insert }) => (
        <div className="space-y-4">
          {schedules.map((option, index) => {
            return (
              <div key={index} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Option header */}
                <div
                  className="flex items-center justify-between flex-wrap gap-3 px-4 py-3"
                  style={{ background: "#F7F8FA", borderBottom: "1px solid #EEF0F3" }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-none"
                      style={{ background: "#FCB614" }}
                    >
                      {index + 1}
                    </span>
                    <Ship size={14} className="text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Freight option {index + 1}</span>
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            insert(index, {
                              ...option,
                              routing: option.routing.map((r) => ({ ...r })),
                            })
                          }
                          title="Duplicate"
                          className="w-8 h-8 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center"
                        >
                          <Copy size={13} />
                        </button>
                        {schedules.length > 1 && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            title="Remove"
                            className="w-8 h-8 rounded-lg bg-red-50 text-red-300 flex items-center justify-center"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Carrier / validity fields */}
                <div className="p-4" style={{ borderBottom: "1px solid #F2F3F5" }}>
                  <div className="grid gap-3" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 0.8fr 1fr 1fr" }}>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Carrier</div>
                      <Field
                        as={SelectMultipleWithSearch}
                        options={carrierList}
                        width="100%"
                        size="middle"
                        name={`schedules.${index}.carrier`}
                        placeholder="Select carrier"
                        handleChange={formikData.handleChange}
                        setFieldValue={setFieldValue}
                        handlePopulateData={(value) =>
                          setFieldValue(`schedules.${index}.routing.0.carrier`, value)
                        }
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Departure</div>
                      <DateTimePicker
                        value={option.departureDateAndTime ? dayjs(option.departureDateAndTime) : null}
                        ampm={false}
                        disablePast
                        disabled={readOnly}
                        onChange={(date) => {
                          setFieldValue(`schedules.${index}.departureDateAndTime`, date);
                          setFieldValue(`schedules.${index}.routing.0.departure`, date);
                          // Arrival before the new departure is no longer valid — clear it
                          // rather than leave a stale, now-impossible date sitting there.
                          const arrivalStillValid =
                            !option.arrivalDateAndTime ||
                            dayjs(option.arrivalDateAndTime).isAfter(dayjs(date));
                          if (!arrivalStillValid) {
                            setFieldValue(`schedules.${index}.arrivalDateAndTime`, null);
                            setFieldValue(`schedules.${index}.routing.0.arrival`, null);
                          }
                          setFieldValue(
                            `schedules.${index}.transitDays`,
                            computeTransitDays(date, arrivalStillValid ? option.arrivalDateAndTime : null)
                          );
                        }}
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "dd/mm/yyyy" } }}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Arrival</div>
                      <DateTimePicker
                        value={option.arrivalDateAndTime ? dayjs(option.arrivalDateAndTime) : null}
                        ampm={false}
                        disabled={readOnly}
                        minDateTime={
                          option.departureDateAndTime
                            ? dayjs(option.departureDateAndTime)
                            : dayjs()
                        }
                        onChange={(date) => {
                          setFieldValue(`schedules.${index}.arrivalDateAndTime`, date);
                          setFieldValue(`schedules.${index}.routing.0.arrival`, date);
                          setFieldValue(
                            `schedules.${index}.transitDays`,
                            computeTransitDays(option.departureDateAndTime, date)
                          );
                        }}
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "dd/mm/yyyy" } }}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Transit</div>
                      <input
                        value={option.transitDays}
                        onChange={(e) =>
                          setFieldValue(
                            `schedules.${index}.transitDays`,
                            e.target.value === "" ? "" : Number(e.target.value)
                          )
                        }
                        onKeyPress={handleKeyPress}
                        placeholder="days"
                        disabled={readOnly}
                        className={CELL_INPUT_CLS}
                      />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Service</div>
                      <select
                        value={option.serviceType}
                        onChange={(e) => setFieldValue(`schedules.${index}.serviceType`, e.target.value)}
                        disabled={readOnly}
                        className={CELL_INPUT_CLS}
                      >
                        {OCEAN_SERVICE_TYPE.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Routing</div>
                      <label className="h-[38px] flex items-center gap-2 text-sm text-gray-700 px-1">
                        <input
                          type="checkbox"
                          checked={!option.isDirect}
                          onChange={(e) => setFieldValue(`schedules.${index}.isDirect`, !e.target.checked)}
                          disabled={readOnly}
                        />
                        Transshipment
                      </label>
                    </div>
                  </div>
                </div>

                {/* Routing legs */}
                <FieldArray name={`schedules.${index}.routing`}>
                  {({ remove: removeLeg, push: pushLeg }) => (
                    <div className="p-4" style={{ borderBottom: "1px solid #F2F3F5" }}>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                        Routing legs
                      </div>
                      <div className="oc-scroll rounded-xl overflow-hidden" style={{ overflowX: "auto", border: "1px solid #EEF0F3" }}>
                        <div style={{ minWidth: 900 }}>
                          <div
                            className="grid"
                            style={{
                              gridTemplateColumns: "1.1fr 1.1fr 1fr 1.3fr 1.3fr 1fr 1fr 90px 50px",
                              background: "#FAFAFB",
                            }}
                          >
                            {["Carrier", "Vessel", "Voyage", "Origin Port", "Destination Port", "Departure", "Arrival", "Transship", ""].map((h) => (
                              <div key={h} className="px-2.5 py-2 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                                {h}
                              </div>
                            ))}
                          </div>
                          {option.routing.map((leg, li) => (
                            <div
                              key={li}
                              className="grid items-center"
                              style={{
                                gridTemplateColumns: "1.1fr 1.1fr 1fr 1.3fr 1.3fr 1fr 1fr 90px 50px",
                                borderTop: "1px solid #F2F3F5",
                              }}
                            >
                              <div className="px-2 py-1.5">
                                <SelectMultipleWithSearch
                                  options={carrierList}
                                  width="100%"
                                  size="middle"
                                  disabled={readOnly || li === 0}
                                  value={leg.carrier}
                                  placeholder="Carrier"
                                  name={`schedules.${index}.routing.${li}.carrier`}
                                  setFieldValue={setFieldValue}
                                />
                              </div>
                              <div className="px-2 py-1.5">
                                <input
                                  value={leg.vesselName}
                                  onChange={(e) =>
                                    setFieldValue(`schedules.${index}.routing.${li}.vesselName`, e.target.value)
                                  }
                                  placeholder="Vessel"
                                  disabled={readOnly}
                                  className={CELL_INPUT_CLS}
                                />
                              </div>
                              <div className="px-2 py-1.5">
                                <input
                                  value={leg.voyageNumber}
                                  onChange={(e) =>
                                    setFieldValue(`schedules.${index}.routing.${li}.voyageNumber`, e.target.value)
                                  }
                                  placeholder="Voyage"
                                  disabled={readOnly}
                                  className={CELL_INPUT_CLS}
                                />
                              </div>
                              <div className="px-2 py-1.5">
                                <PortAutoComplete
                                  name={`schedules.${index}.routing.${li}.originPort`}
                                  value={leg.originPort}
                                  options={portList}
                                  setFieldValue={setFieldValue}
                                  placeholder="Origin port"
                                  inputClassName={CELL_INPUT_CLS}
                                  disabled={readOnly}
                                />
                              </div>
                              <div className="px-2 py-1.5">
                                <PortAutoComplete
                                  name={`schedules.${index}.routing.${li}.destinationPort`}
                                  value={leg.destinationPort}
                                  options={portList}
                                  setFieldValue={setFieldValue}
                                  placeholder="Destination port"
                                  inputClassName={CELL_INPUT_CLS}
                                  disabled={readOnly}
                                />
                              </div>
                              <div className="px-2 py-1.5">
                                {li === 0 ? (
                                  <DateTimePicker
                                    value={leg.departure ? dayjs(leg.departure) : null}
                                    ampm={false}
                                    disabled
                                    format="DD/MM/YYYY HH:mm"
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                  />
                                ) : (
                                  <DatePicker
                                    value={leg.departure ? dayjs(leg.departure) : null}
                                    disablePast
                                    disabled={readOnly}
                                    onChange={(date) =>
                                      setFieldValue(`schedules.${index}.routing.${li}.departure`, date)
                                    }
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "dd/mm/yyyy" } }}
                                  />
                                )}
                              </div>
                              <div className="px-2 py-1.5">
                                {li === 0 ? (
                                  <DateTimePicker
                                    value={leg.arrival ? dayjs(leg.arrival) : null}
                                    ampm={false}
                                    disabled
                                    format="DD/MM/YYYY HH:mm"
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                  />
                                ) : (
                                  <DatePicker
                                    value={leg.arrival ? dayjs(leg.arrival) : null}
                                    disablePast
                                    disabled={readOnly}
                                    onChange={(date) => setFieldValue(`schedules.${index}.routing.${li}.arrival`, date)}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "dd/mm/yyyy" } }}
                                  />
                                )}
                              </div>
                              <div className="px-2 py-1.5 flex justify-center">
                                <input
                                  type="checkbox"
                                  checked={!!leg.transshipment}
                                  onChange={(e) =>
                                    setFieldValue(`schedules.${index}.routing.${li}.transshipment`, e.target.checked)
                                  }
                                  disabled={readOnly}
                                />
                              </div>
                              <div className="px-1.5 py-1.5 flex justify-center">
                                {!readOnly && option.routing.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeLeg(li)}
                                    title="Remove leg"
                                    className="w-7 h-7 rounded-lg bg-red-50 text-red-300 flex items-center justify-center"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => pushLeg(emptyRoutingLeg())}
                          className="mt-2 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-dashed"
                          style={{ borderColor: "#F0C877", color: "#B86E00" }}
                        >
                          <Plus size={12} /> Add leg
                        </button>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>
            );
          })}

          {!readOnly && (
            <button
              type="button"
              onClick={() => push(emptyFreightOption())}
              className="flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              style={{ background: "#FCB614" }}
            >
              <Plus size={15} /> Add freight option
            </button>
          )}
        </div>
      )}
    </FieldArray>
  );
};

export default SchedulesForm;
